import * as React from 'react';

import { FixedSizeList, ListOnItemsRenderedProps } from 'react-window';

import { translate } from '~l10n/client';
import { Action } from '~shared/components/ActionMenu';
import { Container } from '~shared/components/Container';
import { Spinner } from '~shared/components/Spinner';
import { Sort } from '~shared/components/Table/update/components/Sort';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { WorkspaceMembersTableAccessMenu } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/WorkspaceMembersTableAccessMenu';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';

import {
  MembersSortBy,
  MembersSortDir,
  WorkspaceMember,
  WorkspaceRole,
} from '../../../../../api/workspaces/getWorkspaceMembers';
import { assertUnreachable } from '../../../../../util/assertUnreachable';
import { WorkspaceMembersTableWrapper } from './WorkspaceMembersTableWrapper';

const ROW_HEIGHT = 60;
const LIST_HEIGHT = 600;
// Start fetching the next page when the user has scrolled within this many
// rows of the end of what's been fetched.
const FETCH_NEXT_THRESHOLD = 8;

type WorkspaceMemberItem = WorkspaceMember & { isYou: boolean };

export type MembersSort = { by: MembersSortBy; dir: MembersSortDir } | null;

interface Props {
  data: Array<WorkspaceMemberItem>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  sort: MembersSort;
  onSortChange: (column: MembersSortBy) => void;
  children?: React.ReactNode;
  emptyState: React.ReactNode;
  actions: Action[];
  workspaceId: string;
}

const renderRoleLabel = (role: WorkspaceRole): React.ReactNode => {
  switch (role) {
    case WorkspaceRole.Owner:
      return translate('Owner');
    case WorkspaceRole.Admin:
      return translate('Admin');
    case WorkspaceRole.Editor:
      return translate('Editor');
    case WorkspaceRole.Viewer:
      return translate('Viewer');
    default:
      assertUnreachable(role);
  }
};

const renderDisplayName = (member: WorkspaceMemberItem) => {
  const name = member.fullName || translate('Pending login');
  return member.isYou ? translate('{0} (You)').format(name) : name;
};

const headerCellCx =
  'text-xs font-700 leading-8 text-cyan-700 tracking-wider uppercase select-none flex items-center';
const bodyCellCx =
  'flex items-center antialiased text-base text-blue-900 truncate';

interface SortableHeaderProps {
  column: MembersSortBy;
  sort: MembersSort;
  onSortChange: (column: MembersSortBy) => void;
  className: string;
  children: React.ReactNode;
}

function SortableHeader({
  column,
  sort,
  onSortChange,
  className,
  children,
}: SortableHeaderProps) {
  const isActive = sort?.by === column;
  return (
    <div
      className={`${className} cursor-pointer gap-1`}
      onClick={() => onSortChange(column)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSortChange(column);
        }
      }}
    >
      {children}
      <Sort enabled={isActive} descending={isActive && sort?.dir === 'desc'} />
    </div>
  );
}

interface RowProps {
  member: WorkspaceMemberItem;
  showEmail: boolean;
  actions: Action[];
  canRemoveOthers: boolean;
  currentUserId: string;
  currentUserRole: WorkspaceRole;
}

function MemberRow({
  member,
  showEmail,
  actions,
  canRemoveOthers,
  currentUserId,
  currentUserRole,
}: RowProps) {
  return (
    <div
      className="w-full flex flex-row select-none"
      style={{
        height: ROW_HEIGHT,
        borderBottom: '1px solid rgb(232, 237, 240)',
      }}
    >
      <div className={`flex-1 ${bodyCellCx}`}>
        <div className="flex flex-col min-w-0">
          <span className="truncate">{renderDisplayName(member)}</span>
          {showEmail && (
            <span className="truncate text-xs text-blue-700">
              {member.email}
            </span>
          )}
        </div>
      </div>
      <div className={`flex-1 ${bodyCellCx} justify-end`}>
        <WorkspaceMembersTableAccessMenu
          member={member}
          roleLabel={renderRoleLabel(member.role)}
          actions={actions}
          canRemoveOthers={canRemoveOthers}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
        />
      </div>
    </div>
  );
}

export function WorkspaceMembersTable({
  data,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  sort,
  onSortChange,
  children,
  emptyState,
  actions,
  workspaceId,
}: Props) {
  const currentUser = useGetWorkspaceRole({ workspaceId });

  if (data.length === 0 && !hasNextPage) {
    return <>{emptyState}</>;
  }

  if (
    currentUser.data?.id === undefined ||
    currentUser.data?.role === undefined
  ) {
    return <>{children}</>;
  }

  return (
    <LoadedWorkspaceMembersTable
      data={data}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      sort={sort}
      onSortChange={onSortChange}
      actions={actions}
      workspaceId={workspaceId}
      currentUserId={currentUser.data.id}
      currentUserRole={currentUser.data.role}
    >
      {children}
    </LoadedWorkspaceMembersTable>
  );
}

interface LoadedProps {
  data: Array<WorkspaceMemberItem>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  sort: MembersSort;
  onSortChange: (column: MembersSortBy) => void;
  children?: React.ReactNode;
  actions: Action[];
  workspaceId: string;
  currentUserId: string;
  currentUserRole: WorkspaceRole;
}

function LoadedWorkspaceMembersTable({
  data,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  sort,
  onSortChange,
  children,
  actions,
  workspaceId,
  currentUserId,
  currentUserRole,
}: LoadedProps) {
  const applicationContext = useTeacherApplicationContext();
  const showEmail =
    applicationContext !== TeacherApplicationContext.ShowbieAssessments;
  const { permissions } = useWorkspacePermissionsV2({ workspaceId });

  // Render an extra "loading" item at the end while more pages exist.
  const itemCount = data.length + (hasNextPage ? 1 : 0);

  const handleItemsRendered = React.useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (
        hasNextPage &&
        !isFetchingNextPage &&
        visibleStopIndex >= data.length - FETCH_NEXT_THRESHOLD
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, data.length, fetchNextPage]
  );

  const renderRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      // Sentinel "loading" row at the end of the fetched data
      if (index >= data.length) {
        return (
          <div style={style} className="flex items-center justify-center py-4">
            <Spinner />
          </div>
        );
      }
      const member = data[index];
      return (
        <div style={style}>
          <MemberRow
            member={member}
            showEmail={showEmail}
            actions={actions}
            canRemoveOthers={permissions.removeMemberFromWorkspace}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
          />
        </div>
      );
    },
    [
      data,
      showEmail,
      actions,
      permissions.removeMemberFromWorkspace,
      currentUserId,
      currentUserRole,
    ]
  );

  return (
    <Container className={'relative'}>
      {children}
      <WorkspaceMembersTableWrapper>
        {/* Header row — static, sits above the virtualized body */}
        <div
          className="w-full flex flex-row sticky top-0 bg-white z-20"
          style={{
            height: ROW_HEIGHT,
            borderTop: '1px solid rgb(232, 237, 240)',
            borderBottom: '1px solid rgb(232, 237, 240)',
          }}
        >
          <SortableHeader
            column="name"
            sort={sort}
            onSortChange={onSortChange}
            className={`flex-1 ${headerCellCx}`}
          >
            {translate('Name')}
          </SortableHeader>
          <SortableHeader
            column="role"
            sort={sort}
            onSortChange={onSortChange}
            className={`flex-1 ${headerCellCx} justify-end`}
          >
            {translate('Role')}
          </SortableHeader>
        </div>
        <FixedSizeList
          height={LIST_HEIGHT}
          itemCount={itemCount}
          itemSize={ROW_HEIGHT}
          width="100%"
          onItemsRendered={handleItemsRendered}
        >
          {renderRow}
        </FixedSizeList>
      </WorkspaceMembersTableWrapper>
    </Container>
  );
}
